import * as React from 'react';
import { FunctionComponent, PropsWithChildren } from 'react';
import { useParams } from 'react-router';

import { FormControl } from '@mui/material';
import FormHelperText from '@mui/material/FormHelperText';

import { useSyncFileGlobalStateValueWithFormik } from '../../../../../../shared/hooks/use-sync-file-global-state-value-with-formik/use-sync-file-global-state-value-with-formik.hook';
import { useAppSelector } from '../../../../../../store/hooks';
import DragAndDrop from '../../../../../shared/components/drag-and-drop/DragAndDrop';
import { FileMetadataTypesEnum } from '../../../../../shared/enums/file-metadata-types.enum';
import { ApkInfoModel } from '../../../../../shared/models/apk-info.model';
import { mapFileMetadataModelToIdFile } from '../../../../../shared/utils/mapping/mapping.utils';
import { useSetApkDragAndDropTouched } from '../../../../hooks/use-set-apk-drag-and-drop-touched/use-set-apk-drag-and-drop-touched.hook';
import { appsFilesActions } from '../../../../store/slices/apps-files.slice';
import { ExceptedAppsReleaseFormikModel } from '../../models/excepted-apps-release-formik.model';
import { ApkFilesDragAndDropProps } from './types/apk-files-drag-and-drop-props.type';

export const ApkFilesDragAndDrop: FunctionComponent<
  ApkFilesDragAndDropProps
> = (props: PropsWithChildren<ApkFilesDragAndDropProps>) => {
  const {
    errors,
    appsFiles,
    touched,
    values,
    apksIds,
    setFieldValue,
    setFieldTouched,
  } = props;

  const { id: appId } = useParams();
  const parsedAppId = parseInt(appId as string, 10);

  const apkInfo = useAppSelector(
    state => state.appsFeature.releases.apkInfoValues
  ) as ApkInfoModel;

  const formikValuesMapped = values.apkFiles?.map(x => x.id);
  const stateValuesMapped = appsFiles.map(mapFileMetadataModelToIdFile);

  useSyncFileGlobalStateValueWithFormik<ExceptedAppsReleaseFormikModel>(
    setFieldValue,
    apksIds,
    'apkFiles',
    formikValuesMapped,
    stateValuesMapped
  );

  useSetApkDragAndDropTouched<ExceptedAppsReleaseFormikModel>(
    values.apkFiles,
    setFieldTouched,
    'apkFiles'
  );

  return (
    <FormControl sx={{ flexWrap: 'wrap' }}>
      <DragAndDrop
        name="ReleasesFormDnD"
        removeHandler={appsFilesActions.deleteApk}
        uploadHandler={appsFilesActions.setApk}
        multiple={true}
        label="APK Bundles & Files"
        isRequired={true}
        hasError={!!errors.apkFiles && touched.apkFiles}
        {...(appsFiles.length && {
          files: [...appsFiles.map(mapFileMetadataModelToIdFile)],
        })}
        helperText="Drag or drop APK file here to upload"
        buttonLabel={'Browse files'}
        fileTypeAccepted={'.apk'}
        previewType={FileMetadataTypesEnum.APK}
        fileType={FileMetadataTypesEnum.APK}
        splitIntoChunks={true}
        featureId={parsedAppId}
        apkInfo={apkInfo}
      ></DragAndDrop>
      {touched.apkFiles && !!errors.apkFiles && (
        <FormHelperText error>{errors.apkFiles}</FormHelperText>
      )}
    </FormControl>
  );
};
